import { ReactNode } from 'react';

import CommentCardMoreButton from './buttons/MoreButton';
import CardResolveButton from './buttons/CardResolveButton';
import { TComment, TMoreButtonItem } from '../types';
import { COMMENTS_HTML_ATTRIBUTES, MENTIONS_CHILD_CLASSNAMES, NEW_COMMENT_ID } from '../constants';

import { UserLabel } from '@modules/users';
import { TDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import { TPermissions } from '@modules/access';
import { PARSE_DRAFT_CHECKBOX_LABEL_CLASSNAME } from './form/CommentForm';
import { BaseCard } from '@shared/components';
import useCommentStore from '../stores/useCommentStore';
import { TInlineBlock } from '@modules/inlineBlocks';

type TProps = {
  children: ReactNode;
  header?: ReactNode;

  // User ID of the comment author
  userId: string;

  // Possibly undefined if it's a new comment
  item?: TDocument | TFile | TInlineBlock;
  comment?: TComment;
  permissions?: TPermissions;
  commentId?: string;
  moreButtonOptions?: TMoreButtonItem[];
};

const CommentCard = ({
  children,
  header = null,
  item,
  comment,
  commentId = NEW_COMMENT_ID,
  permissions,
  userId,
  moreButtonOptions,
}: TProps) => {
  const { selectedCommentId, setSelectedCommentId } = useCommentStore();

  const isSelected = selectedCommentId === commentId;
  const isNewComment = !(comment && item && permissions && moreButtonOptions);

  // The data-attribute used for scrolling to this card
  const dynamicProps = {
    [COMMENTS_HTML_ATTRIBUTES.COMMENT_CONTAINER_ID]: commentId,
  };

  // Called by BaseCard -> onClick
  const handleSelectionChange = (newSelected: boolean) => {
    setSelectedCommentId(newSelected ? commentId : undefined);
  };

  /**
   * Avoid unselecting the comment when clicking on:
   * - the suggestions window from the MentionsTextarea.tsx
   * - the "Add to draft" checkbox from the CommentForm.tsx
   */
  const ignoreClickSelectors = [
    ...Object.values(MENTIONS_CHILD_CLASSNAMES),
    `.${PARSE_DRAFT_CHECKBOX_LABEL_CLASSNAME}`,
  ];

  return (
    <BaseCard
      selectable={true}
      selected={isSelected}
      onSelectionChange={handleSelectionChange}
      ignoreClickSelectors={ignoreClickSelectors}
      {...dynamicProps}
    >
      {header}

      {/* The user label for the comment’s author */}
      <div className="flex items-center justify-between">
        <UserLabel id={userId} textProps={{ weight: 'semibold' }} avatarProps={{}} className="-ml-6 z-10" />
        <div className="flex items-center gap-1">
          {!isNewComment && (
            <>
              <CardResolveButton userId={userId} item={item} comment={comment} permissions={permissions} />
              <CommentCardMoreButton items={moreButtonOptions} />
            </>
          )}
        </div>
      </div>

      {/* The main content (text, replies, etc.) aligned with the user name */}
      <div className="ml-2.5">{children}</div>
    </BaseCard>
  );
};

export default CommentCard;
